import React, { useState } from 'react';
import { ArrowUpRight, Loader2, Sparkles, Send } from 'lucide-react';
import { db, auth } from '../services/firebase.ts';
import { collection, addDoc } from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';

const CaseStudy: React.FC<{ 
  image?: string; 
  category: string; 
  title: string; 
  outcome: string; 
  url: string;
  imgClassName?: string;
}> = ({ image, category, title, outcome, url, imgClassName = "object-cover" }) => (
  <div className="group cursor-pointer">
    <div className="relative overflow-hidden rounded-[2.5rem] mb-6 aspect-[4/3] shadow-sm border border-[#1F2933]/5 bg-[#F4F1EC]">
      {image ? (
        <img 
          src={image} 
          alt={title} 
          className={`${imgClassName} w-full h-full transform group-hover:scale-105 transition-transform duration-700`}
        />
      ) : (
        <div className="w-full h-full bg-[#E5E2DD] flex items-center justify-center">
          <span className="text-[#1F2933]/20 font-bold uppercase tracking-widest text-[10px]">Image Coming Soon</span>
        </div>
      )}
      <div className="absolute inset-0 bg-[#1F2933] opacity-0 group-hover:opacity-10 transition-opacity pointer-events-none"></div>
    </div>
    <div className="px-1">
      <span className="text-[10px] font-black text-[#7FAF9A] uppercase tracking-[0.2em] mb-2 block">
        {category}
      </span>
      <h3 className="text-xl font-extrabold text-[#1F2933] group-hover:text-[#7FAF9A] transition-colors mb-2">
        <a 
          href={url.startsWith('http') ? url : `https://${url}`} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="hover:underline inline-flex items-center gap-2"
        >
          {title}
          <ArrowUpRight size={16} className="opacity-40 group-hover:opacity-100 transition-opacity" />
        </a>
      </h3>
      <p className="text-[#1F2933]/60 leading-relaxed text-sm font-medium">
        {outcome}
      </p>
    </div>
  </div>
);

const Portfolio: React.FC = () => {
  const [formData, setFormData] = useState({ email: '', mobile: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const ensureUser = async () => {
    if (!auth) throw new Error("Firebase Auth is offline");
    if (!auth.currentUser) await signInAnonymously(auth);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.mobile) return;

    if (!db || !auth) {
      alert("System is currently offline (Demo Mode). Please contact via WhatsApp.");
      return;
    }

    setIsSubmitting(true);
    try {
      await ensureUser();

      // Following Section 5.1: apps/{appId}/records
      await addDoc(collection(db, 'apps/tiny-systems/training_leads'), {
        ...formData,
        date: new Date().toISOString()
      });
      
      setIsSuccess(true);
      setFormData({ email: '', mobile: '' });
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      console.error("Error saving lead:", error);
      alert("Something went wrong. Please try again or use WhatsApp!");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="stories" className="py-32 px-6 lg:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start mb-24 gap-8">
          <div className="max-w-2xl">
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#1F2933] mb-12 leading-[1.1]">
              From ideas to real <span className="text-[#7FAF9A]">systems.</span>
            </h2>
            <div className="mt-4">
              <span className="text-[#7FAF9A] font-black text-lg md:text-2xl uppercase tracking-[0.25em]">
                Samples of my work
              </span>
            </div>
          </div>
          <div className="pt-2 text-right">
            <a 
              href="https://ai.overpowered.co" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-[#7FAF9A] font-black text-lg md:text-xl uppercase tracking-[0.2em] block hover:opacity-70 transition-opacity"
            >
              VISIT MY AI HUB
            </a>
          </div>
        </div>
        
        {/* Grid Section */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16 mb-32">
          <CaseStudy 
            image="https://i.postimg.cc/8zbZTg5q/Screenshot_2025_12_20_213659.png"
            category="AI-powered landing page & assessment"
            title="CAP100 Program"
            outcome="A gentle starting point for people feeling stuck in their careers. It helps them reflect, gain clarity, and decide their next move with confidence."
            url="cap100.overpowered.co"
            imgClassName="object-contain object-right p-8"
          />
          <CaseStudy 
            image="https://i.postimg.cc/vZyPRZYw/Screenshot_2025_12_20_213734.png"
            category="AI-powered Systems Thinking Tool"
            title="LoopLens"
            outcome="A visual space to step back and see the bigger picture. LoopLens helps untangle complex problems, reveal patterns, and make better decisions with calm clarity."
            url="https://loop-lens-six.vercel.app/"
            imgClassName="object-contain object-center p-8"
          />
          <CaseStudy 
            image="https://i.postimg.cc/rwTh6w8B/Screenshot_2025_12_20_213838.png"
            category="AI-powered interactive storybook"
            title="Magic Storybook"
            outcome="An interactive storybook built for my kid. It turns her daily moments into personalised adventures, making bedtime stories feel magical, meaningful, and uniquely hers."
            url="https://alenda-magic-storybook.vercel.app/"
            imgClassName="object-contain object-center p-6"
          />
        </div>

        {/* Founder's Story Section */}
        <div className="bg-[#F4F1EC] rounded-[3rem] p-8 md:p-20 flex flex-col lg:flex-row items-center lg:items-start gap-12 lg:gap-16">
          <div className="w-48 h-48 md:w-64 md:h-64 rounded-full bg-[#E5E2DD] flex-shrink-0 flex items-center justify-center border-4 border-white shadow-xl overflow-hidden relative lg:-mt-10">
             <img 
               src="https://i.postimg.cc/xTLrzmdk/Bagus_TGT_Profile_Pic.png" 
               alt="Founder" 
               className="w-full h-full object-cover object-top"
             />
          </div>
          <div className="flex-1 text-left">
            <h3 className="text-3xl font-black text-[#1F2933] mb-2 tracking-tight">My Tiny Side Hustle.</h3>
            <p className="text-[#7B92B2] font-bold text-sm mb-8 uppercase tracking-[0.1em]">Hello fellow home-based businesses~!</p>
            <div className="space-y-6 text-lg text-[#1F2933]/70 leading-relaxed font-medium mb-12">
              <p>Running a home-based business is harder than it looks.</p>
              <p>You’re responsible for everything — getting leads, managing enquiries, handling orders, and still needing to look credible against bigger companies. Ads only work if they lead to a strong landing page, and without the right systems, good opportunities slip away.</p>
              <p>I learned this the hard way.</p>
              <p>I once spent thousands of dollars on a website that looked fine but didn’t work. It was slow to change, hard to manage, and couldn’t support ads or convert visitors into enquiries — a real problem for a small, home-based business.</p>
              <p>So I decided to build my own systems.</p>
              <p>I learned to design simple, interactive websites and tools that help home-based businesses generate leads, stay lean, and run smarter. I chose this path because I value time with my family. I love my daughter, and being present matters.</p>
              <p>The tools you see here were built for my own business first. I believe the right systems can help home-based founders grow without losing what matters most.</p>
              <p className="font-black text-[#1F2933] pt-4 border-t border-[#1F2933]/5 text-xl">
                If this resonates with you, I’d be happy to work with you.
              </p>
            </div>

            {/* Integrated Mentorship Form */}
            <div id="consultation" className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-[#1F2933]/5 shadow-sm">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7FAF9A]/10 text-[#7FAF9A] text-[9px] font-black uppercase tracking-widest mb-4">
                <Sparkles size={12} />
                <span>Training & Mentorship</span>
              </div>
              
              <h4 className="text-xl md:text-2xl font-extrabold text-[#1F2933] mb-4">
                Learn to build <span className="text-[#7FAF9A]">your own</span> systems.
              </h4>
              
              <p className="text-base text-[#1F2933]/60 mb-6 font-medium">
                I offer consultation sessions and vibecoding trainings. I can teach you how to build your own website / AI-powered tools without prior coding experience.
              </p>

              {isSuccess ? (
                <div className="bg-[#7FAF9A]/10 p-6 rounded-[1.5rem] text-[#7FAF9A] font-bold animate-in zoom-in-95 duration-300">
                  <p className="text-md mb-1">Message received!</p>
                  <p className="text-xs opacity-80">I'll reach out to your mobile shortly to discuss how we can get started.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input 
                      type="email" 
                      required
                      placeholder="Email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="flex-1 px-6 py-4 rounded-2xl border border-[#1F2933]/10 focus:outline-none focus:ring-2 focus:ring-[#7FAF9A] bg-[#F4F1EC]/30 font-medium text-sm"
                    />
                    <input 
                      type="tel" 
                      required
                      placeholder="Mobile"
                      value={formData.mobile}
                      onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                      className="flex-1 px-6 py-4 rounded-2xl border border-[#1F2933]/10 focus:outline-none focus:ring-2 focus:ring-[#7FAF9A] bg-[#F4F1EC]/30 font-medium text-sm"
                    />
                  </div>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-[#1F2933] text-white py-4 rounded-2xl font-bold text-md hover:bg-black transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isSubmitting ? <Loader2 size={20} className="animate-spin" /> : (
                      <>
                        Inquire about training
                        <Send size={16} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;